<template>
    <div class="ui-page">
        <ui-page-header :title="page.title" back="/impugnacoes" back-text="Impugnações">
            <v-menu offset-y v-if="!page.isNew">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn elevation="0" outlined v-bind="attrs" v-on="on"
                        :color="dataset.data.status === 1 ? 'success' : 'primary'">
                        <v-icon left small>{{ dataset.data.status === 1 ? 'mdi-check-circle' : 'mdi-progress-clock' }}</v-icon>
                        {{ dataset.data.status === 1 ? 'Finalizado' : 'Em andamento' }}
                        <v-icon right small>mdi-chevron-down</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item @click="changeStatus(0)" :disabled="dataset.data.status === 0">
                        <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-progress-clock</v-icon></v-list-item-icon>
                        <v-list-item-title>Em andamento</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="changeStatus(1)" :disabled="dataset.data.status === 1">
                        <v-list-item-icon class="mr-2"><v-icon small color="success">mdi-check-circle</v-icon></v-list-item-icon>
                        <v-list-item-title>Finalizado</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn v-if="$allowed('save')" id="btnSave" type="button" depressed color="primary" @click="save"
                :loading="dataForm.saveLoading">Salvar</v-btn>
        </ui-page-header>

        <v-form :readonly="!$allowed('save')" ref="form" v-model="dataForm.validForm">
            <!-- Card 1: Identificação -->
            <ui-section title="Identificação">
                <v-row class="mb-4">
                    <v-col cols="12">
                        <v-btn-toggle v-model="dataset.data.mode" mandatory dense class="btn-toggle-gap">
                            <v-btn small value="detailed">
                                <v-icon left small>mdi-format-list-text</v-icon>
                                Detalhado
                            </v-btn>
                            <v-btn small value="summary">
                                <v-icon left small>mdi-text-short</v-icon>
                                Resumido
                            </v-btn>
                        </v-btn-toggle>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="4">
                        <v-autocomplete dense :items="dataset.clients" label="Caso"
                            v-model="dataset.data.client_id" item-value="id" item-text="name"
                            outlined hide-details="auto" required :rules="[$rules.required]"
                            no-data-text="Nenhum caso encontrado">
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field dense label="Nº Processo" v-model="dataset.data.process_number"
                            required :rules="[$rules.required]" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select dense :items="dataset.proceedingTypes" label="Tipo"
                            v-model="dataset.data.type" item-value="id" item-text="name"
                            outlined hide-details="auto" required :rules="[$rules.required]">
                        </v-select>
                    </v-col>
                </v-row>
            </ui-section>

            <!-- Card 2: Participantes e Créditos -->
            <ui-section title="Participantes e Créditos">
                <v-list v-if="dataset.data.participants && dataset.data.participants.length > 0" dense>
                    <template v-for="(participant, pIndex) in dataset.data.participants">
                        <v-list-item :key="'p-' + pIndex">
                            <v-list-item-avatar>
                                <v-icon size="32">mdi-account-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>
                                    <ui-badge tone="primary" class="mr-2">{{ participant.role | enum(dataset.participantRoles, "id", "name") }}</ui-badge>
                                    {{ participant.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ participant.registration || '' }}
                                    <span v-if="participant.position"> &mdash; {{ participant.position === 'autor' || Number(participant.position) === 1 ? 'Autor' : 'Réu' }}</span>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn v-if="participant.role === 1" icon small color="primary"
                                        @click="openCreditDialog(participant, pIndex)">
                                        <v-icon size="18">mdi-plus-circle-outline</v-icon>
                                    </v-btn>
                                    <v-btn icon small @click="removeParticipant(participant, pIndex)">
                                        <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                                    </v-btn>
                                </div>
                            </v-list-item-action>
                        </v-list-item>

                        <!-- Credits sub-list for creditors (role=1) -->
                        <template v-if="participant.role === 1 && participant.credits && participant.credits.length > 0">
                            <v-list-item v-for="(credit, cIndex) in participant.credits"
                                :key="'p-' + pIndex + '-c-' + cIndex" class="pl-12">
                                <v-list-item-avatar>
                                    <v-icon size="20" color="green">mdi-cash</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        R$ {{ credit.value | currency }}
                                        <span class="ui-muted ml-2">
                                            {{ credit.class | enum(dataset.creditorTypes, "id", "name") }}
                                        </span>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ credit.currency | enum(dataset.currencyTypes, "id", "name") }}
                                        <span v-if="credit.pretension"> &mdash; {{ credit.pretension | enum(dataset.pretensionTypes, "id", "name") }}</span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-btn icon small @click="removeCredit(participant, pIndex, credit, cIndex)">
                                        <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                        </template>

                        <v-divider :key="'d-' + pIndex" class="my-1"></v-divider>
                    </template>
                </v-list>
                <div v-else class="ui-muted pa-2">Nenhum participante adicionado.</div>

                <v-row class="mt-2">
                    <v-col cols="12">
                        <v-btn v-if="$allowed('create')" color="primary" depressed small @click="openParticipantDialog">
                            <v-icon left small>mdi-plus</v-icon>
                            Adicionar participante
                        </v-btn>
                    </v-col>
                </v-row>
            </ui-section>

            <!-- Card 3: Manifestações -->
            <ui-section title="Manifestações">
                <v-list v-if="dataset.data.manifestations && dataset.data.manifestations.length > 0" dense>
                    <v-list-item v-for="(manifestation, mIndex) in dataset.data.manifestations" :key="'m-' + mIndex">
                        <v-list-item-avatar>
                            <v-icon size="28">mdi-file-document-outline</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>
                                <ui-badge tone="primary" :dot="false" class="mr-2" :text="getParticipantName(manifestation.participant_id)" />
                                <span v-if="manifestation.ref" class="font-weight-medium">REF: {{ manifestation.ref }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-truncate" style="max-width: 500px;">
                                {{ manifestation.description }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="removeManifestation(manifestation, mIndex)">
                                <v-icon size="18" color="error">mdi-delete-outline</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
                <div v-else class="ui-muted pa-2">Nenhuma manifestação adicionada.</div>

                <v-row class="mt-2">
                    <v-col cols="12">
                        <v-btn v-if="$allowed('create')" color="primary" depressed small @click="openManifestationDialog">
                            <v-icon left small>mdi-plus</v-icon>
                            Adicionar manifestação
                        </v-btn>
                    </v-col>
                </v-row>
            </ui-section>

            <!-- Card 4: Sentença -->
            <ui-section title="Sentença">
                <v-row>
                    <v-col cols="12">
                        <v-switch v-model="dataset.data.has_sentence" label="Houve sentença?"
                            hide-details class="mt-0"></v-switch>
                    </v-col>
                </v-row>
                <template v-if="dataset.data.has_sentence">
                    <v-row>
                        <v-col cols="12">
                            <v-textarea dense label="Resultado" v-model="dataset.data.sentence_result"
                                outlined hide-details="auto" rows="3">
                            </v-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-switch v-model="dataset.data.sentence_changed_credit"
                                label="Houve alteração no crédito?" hide-details class="mt-0"></v-switch>
                        </v-col>
                    </v-row>
                    <template v-if="dataset.data.sentence_changed_credit">
                        <v-row>
                            <v-col cols="12">
                                <div class="d-flex align-center justify-space-between mb-2">
                                    <span class="font-weight-medium">Créditos alterados pela sentença</span>
                                    <v-btn v-if="$allowed('create')" color="primary" depressed small @click="addSentenceCredit">
                                        <v-icon left small>mdi-plus</v-icon>
                                        Adicionar crédito
                                    </v-btn>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row v-for="(credit, index) in dataset.data.sentence_credits" :key="'sentence-credit-' + index" class="align-center">
                            <v-col cols="12" md="3">
                                <v-select dense :items="sentenceParticipantSelectItems" label="Participante"
                                    v-model="credit.participant_id" item-value="id" item-text="name"
                                    outlined hide-details="auto">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-currency-field dense label="Valor final" v-model="credit.value"
                                    :allow-negative="false" outlined hide-details="auto">
                                </v-currency-field>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-select dense :items="dataset.creditorTypes" label="Classe final"
                                    v-model="credit.class" item-value="id" item-text="name"
                                    outlined hide-details="auto">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-select dense :items="dataset.currencyTypes" label="Moeda"
                                    v-model="credit.currency" item-value="id" item-text="name"
                                    outlined hide-details="auto">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="1" class="d-flex justify-end">
                                <v-btn icon small @click="removeSentenceCredit(index)">
                                    <v-icon color="error">mdi-delete-outline</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>

                        <div v-if="!dataset.data.sentence_credits || dataset.data.sentence_credits.length === 0"
                            class="ui-muted pa-2">
                            Nenhum crédito da sentença adicionado.
                        </div>
                    </template>
                    <v-row v-if="!page.isNew && (dataset.data.sentence_changed_credit || (effectiveAppeal && effectiveAppeal.changed_credit))" class="mt-2">
                        <v-col cols="12">
                            <v-btn color="primary" depressed small @click="applyQgc"
                                :loading="dataForm.applyQgcLoading"
                                :disabled="dataset.data.qgc_updated">
                                <v-icon left small>{{ dataset.data.qgc_updated ? 'mdi-check-circle' : 'mdi-check-circle-outline' }}</v-icon>
                                {{ dataset.data.qgc_updated ? 'QGC atualizado' : 'Autorizar alteração QGC' }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </template>
            </ui-section>

            <!-- Card 5: Recursos (varios por impugnacao) -->
            <ui-section title="Recursos">
                <v-row>
                    <v-col cols="12">
                        <v-switch v-model="dataset.data.has_appeal" label="Houve recurso?"
                            hide-details class="mt-0" @change="onHasAppealChange"></v-switch>
                    </v-col>
                </v-row>
                <template v-if="dataset.data.has_appeal">
                    <v-row>
                        <v-col cols="12">
                            <div class="d-flex align-center justify-space-between mb-2">
                                <span class="font-weight-medium">Recursos interpostos ({{ dataset.data.appeals.length }})</span>
                                <v-btn v-if="$allowed('create')" color="primary" depressed small @click="addAppeal">
                                    <v-icon left small>mdi-plus</v-icon>
                                    Adicionar recurso
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>

                    <v-card v-for="(appeal, index) in dataset.data.appeals" :key="'appeal-' + index" outlined class="mb-3 pa-3">
                        <v-row class="align-center">
                            <v-col cols="12" md="4">
                                <v-combobox dense :items="appealNameSuggestions" :label="'Recurso ' + (index + 1)"
                                    v-model="appeal.name" outlined hide-details="auto"
                                    placeholder="Ex.: Agravo de instrumento">
                                </v-combobox>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select dense :items="dataset.appealResults" label="Resultado"
                                    v-model="appeal.result" item-value="id" item-text="name"
                                    outlined hide-details="auto" clearable>
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field dense type="date" label="Data da decisão" v-model="appeal.decided_at"
                                    outlined hide-details="auto" clearable></v-text-field>
                            </v-col>
                            <v-col cols="12" md="1" class="d-flex justify-end">
                                <v-btn v-if="$allowed('delete')" icon small @click="removeAppeal(index)" title="Remover recurso">
                                    <v-icon color="error">mdi-delete-outline</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-switch v-model="appeal.changed_credit" label="Houve alteração no crédito?"
                                    hide-details class="mt-0"></v-switch>
                            </v-col>
                        </v-row>
                        <v-row v-if="appeal.changed_credit">
                            <v-col cols="12" md="4">
                                <v-currency-field dense label="Valor final" v-model="appeal.final_value"
                                    :allow-negative="false" outlined hide-details="auto">
                                </v-currency-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select dense :items="dataset.creditorTypes" label="Classe final"
                                    v-model="appeal.final_class" item-value="id" item-text="name"
                                    outlined hide-details="auto">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select dense :items="dataset.currencyTypes" label="Moeda"
                                    v-model="appeal.final_currency" item-value="id" item-text="name"
                                    outlined hide-details="auto">
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-card>

                    <div v-if="!dataset.data.appeals.length" class="ui-muted pa-2">
                        Nenhum recurso adicionado.
                    </div>
                    <v-alert v-if="effectiveAppeal && effectiveAppeal.changed_credit" dense text type="info" class="mt-2 mb-0">
                        Para o QGC prevalece o último recurso que alterou o crédito:
                        <strong>{{ effectiveAppeal.name || ('Recurso ' + (dataset.data.appeals.indexOf(effectiveAppeal) + 1)) }}</strong>.
                    </v-alert>
                </template>
            </ui-section>

            <!-- Card 6: Andamento -->
            <ui-section title="Andamento">
                <v-row>
                    <v-col cols="12">
                        <v-switch v-model="dataset.data.final_judgment" label="Trânsito em julgado?"
                            hide-details class="mt-0"></v-switch>
                    </v-col>
                </v-row>
            </ui-section>

            <!-- Publicacoes do DJEN pelo numero do processo -->
            <djen-publications v-if="!page.isNew && $route.params.id" module="proceeding" :record-id="$route.params.id" :can-edit="$allowed('edit')" @changed="load" />

            <!-- Card 7: Comentários internos -->
            <ui-section title="Comentários internos">
                <v-list v-if="dataset.data.comments && dataset.data.comments.length > 0" dense>
                    <v-list-item v-for="(comment, cIdx) in dataset.data.comments" :key="'comment-' + cIdx">
                        <v-list-item-avatar>
                            <v-icon size="28">mdi-comment-outline</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title class="font-weight-medium">
                                {{ comment.admin ? comment.admin.name : 'Admin' }}
                                <span class="ui-muted ml-2 caption">
                                    {{ formatDate(comment.created_at) }}
                                </span>
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-wrap">{{ comment.content }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
                <div v-else class="ui-muted pa-2">Nenhum comentário adicionado.</div>

                <v-row class="mt-3" v-if="!page.isNew">
                    <v-col cols="12" md="10">
                        <v-text-field dense label="Novo comentário" v-model="newComment"
                            outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center">
                        <v-btn color="primary" depressed small @click="addComment"
                            :loading="dataForm.commentLoading" :disabled="!newComment">
                            Comentar
                        </v-btn>
                    </v-col>
                </v-row>
            </ui-section>
        </v-form>

        <!-- Dialog: Adicionar participante -->
        <v-dialog v-model="dialogParticipant" max-width="600px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Adicionar participante</h3>
                </div>
                <div class="ui-card__body">
                    <v-form :readonly="!$allowed('save')" ref="participantForm" v-model="dataForm.participantFormValid">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select dense :items="dataset.participantRoles" label="Papel"
                                    v-model="participantForm.role" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select dense :items="[{id: 1, name: 'Autor'}, {id: 2, name: 'Réu'}]"
                                    label="Posição" v-model="participantForm.position" item-value="id"
                                    item-text="name" outlined hide-details="auto" clearable>
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-row class="mb-2">
                            <v-col cols="12">
                                <v-btn-toggle v-model="participantForm.source" mandatory dense class="btn-toggle-gap">
                                    <v-btn small value="qgc">
                                        <v-icon left small>mdi-account-search</v-icon>
                                        Já cadastrado no QGC
                                    </v-btn>
                                    <v-btn v-if="$allowed('create')" small value="new">
                                        <v-icon left small>mdi-account-plus</v-icon>
                                        Novo
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>
                        </v-row>
                        <template v-if="participantForm.source === 'qgc'">
                            <v-row>
                                <v-col cols="12">
                                    <v-autocomplete dense :items="dataset.userCreditors" label="Buscar credor no QGC"
                                        v-model="participantForm.user_creditor_id" item-value="id" item-text="name"
                                        outlined hide-details="auto" :rules="[$rules.required]"
                                        no-data-text="Nenhum credor encontrado"
                                        :loading="dataForm.userCreditorsLoading"
                                        @update:search-input="searchUserCreditors">
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                        </template>
                        <template v-if="participantForm.source === 'new'">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field dense label="Nome" v-model="participantForm.name"
                                        outlined hide-details="auto" :rules="[$rules.required]">
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field dense label="CPF/CNPJ" v-model="participantForm.registration"
                                        outlined hide-details="auto">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </template>
                    </v-form>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="dialogParticipant = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed @click="confirmAddParticipant"
                        :loading="dataForm.participantLoading">Adicionar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Dialog: Adicionar crédito -->
        <v-dialog v-model="dialogCredit" max-width="600px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Adicionar crédito</h3>
                </div>
                <div class="ui-card__body">
                    <v-form :readonly="!$allowed('save')" ref="creditForm" v-model="dataForm.creditFormValid">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-currency-field dense label="Valor" v-model="creditForm.value"
                                    :allow-negative="false" outlined hide-details="auto" :rules="[$rules.required]">
                                </v-currency-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select dense :items="dataset.creditorTypes" label="Classe"
                                    v-model="creditForm.creditor_class" item-value="id" item-text="name"
                                    :disabled="!dataset.data.client_id"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select dense :items="dataset.currencyTypes" label="Moeda"
                                    v-model="creditForm.currency" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select dense :items="dataset.pretensionTypes" label="Pretensão"
                                    v-model="creditForm.pretension" item-value="id" item-text="name"
                                    outlined hide-details="auto" clearable>
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="dialogCredit = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed @click="confirmAddCredit"
                        :loading="dataForm.creditLoading">Adicionar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Dialog: Adicionar manifestação -->
        <v-dialog v-model="dialogManifestation" max-width="700px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Adicionar manifestação</h3>
                </div>
                <div class="ui-card__body">
                    <v-form :readonly="!$allowed('save')" ref="manifestationForm" v-model="dataForm.manifestationFormValid">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select dense :items="participantSelectItems" label="Participante"
                                    v-model="manifestationForm.participant_id" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field dense label="Ref." v-model="manifestationForm.ref"
                                    outlined hide-details="auto">
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-textarea dense label="Descrição" v-model="manifestationForm.description"
                                    outlined hide-details="auto" rows="3" :rules="[$rules.required]">
                                </v-textarea>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-currency-field dense label="Valor do crédito" v-model="manifestationForm.credit_value"
                                    :allow-negative="false" outlined hide-details="auto">
                                </v-currency-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select dense :items="dataset.creditorTypes" label="Classe"
                                    v-model="manifestationForm.credit_class" item-value="id" item-text="name"
                                    :disabled="!dataset.data.client_id"
                                    outlined hide-details="auto" clearable>
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select dense :items="dataset.currencyTypes" label="Moeda"
                                    v-model="manifestationForm.credit_currency" item-value="id" item-text="name"
                                    outlined hide-details="auto" clearable>
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="dialogManifestation = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed @click="confirmAddManifestation"
                        :loading="dataForm.manifestationLoading">Adicionar</v-btn>
                </div>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import axios from "axios";

import proceedingTypes from "@/common/data/proceeding-types.data";
import appealResults from "@/common/data/proceeding-appeal-results.data";
import participantRoles from "@/common/data/proceeding-participant-roles.data";
import creditorTypes from "@/common/data/creditor-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import currencyTypes from "@/common/data/currency-types.data";
import pretensionTypes from "@/common/data/incident-pretension-types.data";
import DjenPublications from "@/components/djen/DjenPublications.vue";

export default {
    name: "ProceedingDetails",

    components: { DjenPublications },

    data: () => ({
        page: {
            isNew: false,
            title: "",
        },

        newComment: "",

        // Dialog visibility flags
        dialogParticipant: false,
        dialogCredit: false,
        dialogManifestation: false,

        // Credit dialog context
        creditParticipant: null,
        creditParticipantIndex: null,

        // Forms inside dialogs
        participantForm: {
            role: null,
            position: null,
            source: "qgc",
            user_creditor_id: null,
            name: "",
            registration: "",
        },
        creditForm: {
            value: null,
            creditor_class: null,
            currency: null,
            pretension: null,
        },
        manifestationForm: {
            participant_id: null,
            ref: "",
            description: "",
            credit_value: null,
            credit_class: null,
            credit_currency: null,
        },

        dataset: {
            breadcrumbItems: [],
            clients: [],
            userCreditors: [],
            proceedingTypes: proceedingTypes,
            appealResults: appealResults,
            participantRoles: participantRoles,
            creditorTypes: creditorTypes,
            currencyTypes: currencyTypes,
            pretensionTypes: pretensionTypes,
            data: {
                mode: "detailed",
                client_id: null,
                process_number: "",
                type: null,
                participants: [],
                manifestations: [],
                comments: [],
                has_sentence: false,
                sentence_result: "",
                sentence_changed_credit: false,
                sentence_credits: [],
                sentence_final_value: null,
                sentence_final_class: null,
                sentence_final_currency: null,
                has_appeal: false,
                appeal_result: null,
                appeal_changed_credit: false,
                appeal_final_value: null,
                appeal_final_class: null,
                appeal_final_currency: null,
                // Lista de recursos (substitui os campos unicos acima, que passam a ser derivados)
                appeals: [],
                final_judgment: false,
            },
        },
        appealNameSuggestions: ['Agravo de instrumento', 'Apelação', 'Embargos de declaração', 'Agravo interno', 'Recurso especial', 'Recurso extraordinário'],

        dataForm: {
            validForm: true,
            saveLoading: false,
            participantLoading: false,
            creditLoading: false,
            manifestationLoading: false,
            commentLoading: false,
            applyQgcLoading: false,
            userCreditorsLoading: false,
            participantFormValid: true,
            creditFormValid: true,
            manifestationFormValid: true,
        },
    }),

    computed: {
        // Ultimo recurso que alterou o credito (ou o ultimo recurso), como a API deriva appeal_*
        effectiveAppeal() {
            const appeals = (this.dataset.data && this.dataset.data.has_appeal && this.dataset.data.appeals) || [];
            if (!appeals.length) return null;
            return appeals.slice().reverse().find(item => item.changed_credit) || appeals[appeals.length - 1];
        },

        participantSelectItems() {
            if (!this.dataset.data.participants) return [];
            return this.dataset.data.participants.map(p => ({
                id: p.id || p._tempId,
                name: p.name || "Participante",
            }));
        },

        sentenceParticipantSelectItems() {
            if (!this.dataset.data.participants) return [];
            return this.dataset.data.participants
                .filter(p => p.role === 1)
                .map(p => ({
                    id: p.id || p._tempId,
                    name: p.name || "Participante",
                }));
        },
    },

    created() {
        this.loadData();
    },

    watch: {
        'dataset.data.client_id'(value, oldValue) {
            if (String(value) === String(oldValue)) return;

            this.loadCreditorClasses();
        },
    },

    methods: {
        async loadCreditorClasses() {
            // Inclui ocultas: creditos ja vinculados mantem o nome da classe.
            this.dataset.creditorTypes = await creditorClassUtil.listLabels(this.dataset.data.client_id);
        },

        async loadData() {
            this.setPage();
            this.listBreadCrumbItems();
            await this.loadClients();
            await this.load();
        },

        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = this.page.isNew ? "Nova impugnação" : "Detalhes da impugnação";
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Impugnações", disabled: false, to: "/impugnacoes", exact: true },
                { text: this.page.isNew ? "Nova impugnação" : "Detalhes", disabled: true, exact: true },
            ];
        },

        formatDate(date) {
            if (!date) return "";
            return this.$options.filters.defaultDate(date, "DD/MM/YYYY HH:mm");
        },

        getParticipantName(participantId) {
            if (!this.dataset.data.participants) return "—";
            const p = this.dataset.data.participants.find(x => (x.id || x._tempId) === participantId);
            return p ? p.name : "—";
        },

        goBack() {
            this.$router.replace("/impugnacoes");
        },

        // ---- Load data ----

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const data = result.data.content.data;
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async load() {
            if (this.page.isNew) return;

            try {
                const result = await axios.get(`/api/admin/proceedings/${this.$route.params.id}`);
                const data = result.data.content || result.data;

                this.dataset.data = {
                    ...this.dataset.data,
                    ...data,
                    participants: data.participants || [],
                    manifestations: data.manifestations || [],
                    comments: data.comments || [],
                    sentence_credits: this.normalizeSentenceCredits(data),
                    appeals: this.normalizeAppeals(data),
                    // A coluna e numerica (1/2); o toggle usa 'detailed'/'summary'
                    mode: ({ 1: 'detailed', 2: 'summary' })[data.mode] || data.mode || 'detailed',
                };
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async searchUserCreditors(query) {
            if (!query || query.length < 2) return;

            this.dataForm.userCreditorsLoading = true;
            try {
                const result = await axios.get("/api/admin/user-creditors", { params: { search: { name: query }, start: 0, limit: 50 } });
                this.dataset.userCreditors = result.data.content.data || [];
            } catch (error) {
                // silent
            } finally {
                this.dataForm.userCreditorsLoading = false;
            }
        },

        // ---- Save (create/update) main proceeding ----

        async changeStatus(status) {
            try {
                await axios.patch(`/api/admin/proceedings/${this.$route.params.id}`, { status });
                this.$set(this.dataset.data, 'status', status);
                this.$dialog.notify.info("Status atualizado!", { position: "top-right", timeout: 2000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            const action = this.page.isNew ? "incluir" : "atualizar";

            const proceedAction = await this.$dialog.confirm({
                text: `Deseja ${action} a impugnação?`,
                title: `${this.page.isNew ? 'Incluir' : 'Atualizar'} impugnação`,
                actions: { false: "Cancelar", true: this.page.isNew ? "Salvar" : "Atualizar" },
            });

            if (!proceedAction) return;

            this.dataForm.saveLoading = true;

            const payload = this.buildPayload();

            try {
                if (this.page.isNew) {
                    const response = await axios.post("/api/admin/proceedings", payload);
                    const created = response.data.content || response.data;
                    this.$dialog.notify.info("Impugnação criada com sucesso!", { position: "top-right", timeout: 2000 });
                    this.$router.replace(`/impugnacoes/${created.id}`);
                } else {
                    await axios.patch(`/api/admin/proceedings/${this.$route.params.id}`, payload);
                    this.$dialog.notify.info("Impugnação atualizada com sucesso!", { position: "top-right", timeout: 2000 });
                    this.load();
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.saveLoading = false;
            }
        },

        buildPayload() {
            const d = this.dataset.data;
            return {
                client_id: d.client_id,
                process_number: d.process_number,
                type: d.type,
                mode: d.mode,
                has_sentence: d.has_sentence,
                sentence_result: d.sentence_result,
                sentence_changed_credit: d.sentence_changed_credit,
                sentence_credits: (d.sentence_credits || []).map(item => ({
                    participant_id: item.participant_id,
                    value: item.value,
                    class: item.class,
                    currency: item.currency || 1,
                })),
                sentence_final_value: d.sentence_final_value,
                sentence_final_class: d.sentence_final_class,
                sentence_final_currency: d.sentence_final_currency,
                has_appeal: d.has_appeal,
                appeals: d.has_appeal ? (d.appeals || []).map(item => ({
                    name: item.name || null,
                    result: item.result || null,
                    changed_credit: !!item.changed_credit,
                    final_value: item.changed_credit ? item.final_value : null,
                    final_class: item.changed_credit ? item.final_class : null,
                    final_currency: item.changed_credit ? item.final_currency : null,
                    decided_at: item.decided_at || null,
                })) : [],
                final_judgment: d.final_judgment,
            };
        },

        // ---- Recursos ----
        normalizeAppeals(data) {
            const appeals = Array.isArray(data.appeals) ? data.appeals : [];
            if (appeals.length) {
                return appeals.map(item => ({
                    id: item.id,
                    name: item.name || '',
                    result: item.result || null,
                    changed_credit: !!item.changed_credit,
                    final_value: item.final_value,
                    final_class: item.final_class,
                    final_currency: item.final_currency || null,
                    decided_at: item.decided_at ? String(item.decided_at).slice(0, 10) : null,
                }));
            }
            // Registro antigo, com recurso unico nas colunas appeal_*
            if (data.has_appeal) {
                return [{
                    name: 'Recurso',
                    result: data.appeal_result || null,
                    changed_credit: !!data.appeal_changed_credit,
                    final_value: data.appeal_final_value,
                    final_class: data.appeal_final_class,
                    final_currency: data.appeal_final_currency || null,
                    decided_at: null,
                }];
            }
            return [];
        },

        addAppeal() {
            this.dataset.data.appeals.push({ name: '', result: null, changed_credit: false, final_value: null, final_class: null, final_currency: 1, decided_at: null });
        },

        removeAppeal(index) {
            this.dataset.data.appeals.splice(index, 1);
        },

        onHasAppealChange(value) {
            if (value && !this.dataset.data.appeals.length) this.addAppeal();
        },

        normalizeSentenceCredits(data) {
            const credits = data.sentence_credits || data.sentenceCredits || [];

            if (credits.length > 0) {
                return credits.map(item => ({
                    id: item.id,
                    participant_id: item.participant_id,
                    value: item.value,
                    class: item.class,
                    currency: item.currency || 1,
                }));
            }

            if (data.sentence_changed_credit && (data.sentence_final_value || data.sentence_final_class || data.sentence_final_currency)) {
                const firstCreditorParticipant = (data.participants || []).find(item => item.role === 1);
                return [{
                    participant_id: firstCreditorParticipant ? firstCreditorParticipant.id : null,
                    value: data.sentence_final_value,
                    class: data.sentence_final_class,
                    currency: data.sentence_final_currency || 1,
                }];
            }

            return [];
        },

        addSentenceCredit() {
            if (!this.dataset.data.sentence_credits) {
                this.$set(this.dataset.data, "sentence_credits", []);
            }

            this.dataset.data.sentence_credits.push({
                participant_id: this.sentenceParticipantSelectItems.length === 1 ? this.sentenceParticipantSelectItems[0].id : null,
                value: null,
                class: null,
                currency: 1,
            });
        },

        removeSentenceCredit(index) {
            this.dataset.data.sentence_credits.splice(index, 1);
        },

        // ---- Participants ----

        openParticipantDialog() {
            this.participantForm = {
                role: null,
                position: null,
                source: "qgc",
                user_creditor_id: null,
                name: "",
                registration: "",
            };
            this.dialogParticipant = true;
        },

        async confirmAddParticipant() {
            if (!this.$refs.participantForm.validate()) return;

            if (this.page.isNew) {
                // Add locally when creating
                const tempParticipant = {
                    _tempId: Date.now(),
                    role: this.participantForm.role,
                    position: this.participantForm.position,
                    source: this.participantForm.source,
                    user_creditor_id: this.participantForm.user_creditor_id,
                    name: this.participantForm.source === "qgc"
                        ? (this.dataset.userCreditors.find(x => x.id === this.participantForm.user_creditor_id) || {}).name || "Credor QGC"
                        : this.participantForm.name,
                    registration: this.participantForm.registration,
                    credits: [],
                };
                this.dataset.data.participants.push(tempParticipant);
                this.dialogParticipant = false;
                return;
            }

            this.dataForm.participantLoading = true;

            try {
                const payload = {
                    role: this.participantForm.role,
                    position: this.participantForm.position,
                    source: this.participantForm.source,
                    user_creditor_id: this.participantForm.user_creditor_id,
                    name: this.participantForm.name,
                    registration: this.participantForm.registration,
                };

                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/participants`, payload);
                this.$dialog.notify.info("Participante adicionado com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogParticipant = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.participantLoading = false;
            }
        },

        async removeParticipant(participant, index) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir este participante?",
                title: "Excluir participante",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !participant.id) {
                this.dataset.data.participants.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/participants/${participant.id}`);
                this.$dialog.notify.info("Participante removido com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Credits ----

        openCreditDialog(participant, pIndex) {
            this.creditParticipant = participant;
            this.creditParticipantIndex = pIndex;
            this.creditForm = {
                value: null,
                creditor_class: null,
                currency: null,
                pretension: null,
            };
            this.dialogCredit = true;
        },

        async confirmAddCredit() {
            if (!this.$refs.creditForm.validate()) return;

            if (this.page.isNew || !this.creditParticipant.id) {
                // Add locally
                if (!this.creditParticipant.credits) {
                    this.$set(this.creditParticipant, "credits", []);
                }
                this.creditParticipant.credits.push({ ...this.creditForm });
                this.dialogCredit = false;
                return;
            }

            this.dataForm.creditLoading = true;

            try {
                const payload = {
                    value: this.creditForm.value,
                    class: this.creditForm.creditor_class,
                    currency: this.creditForm.currency,
                    pretension: this.creditForm.pretension,
                };
                await axios.post(
                    `/api/admin/proceedings/participants/${this.creditParticipant.id}/credits`,
                    payload
                );
                this.$dialog.notify.info("Crédito adicionado com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogCredit = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.creditLoading = false;
            }
        },

        async removeCredit(participant, pIndex, credit, cIndex) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir este crédito?",
                title: "Excluir crédito",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !credit.id) {
                participant.credits.splice(cIndex, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/credits/${credit.id}`);
                this.$dialog.notify.info("Crédito removido com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Manifestations ----

        openManifestationDialog() {
            this.manifestationForm = {
                participant_id: null,
                ref: "",
                description: "",
                credit_value: null,
                credit_class: null,
                credit_currency: null,
            };
            this.dialogManifestation = true;
        },

        async confirmAddManifestation() {
            if (!this.$refs.manifestationForm.validate()) return;

            if (this.page.isNew) {
                this.dataset.data.manifestations.push({ ...this.manifestationForm });
                this.dialogManifestation = false;
                return;
            }

            this.dataForm.manifestationLoading = true;

            try {
                await axios.post(
                    `/api/admin/proceedings/${this.$route.params.id}/manifestations`,
                    this.manifestationForm
                );
                this.$dialog.notify.info("Manifestação adicionada com sucesso!", { position: "top-right", timeout: 2000 });
                this.dialogManifestation = false;
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.manifestationLoading = false;
            }
        },

        async removeManifestation(manifestation, index) {
            const proceedAction = await this.$dialog.confirm({
                text: "Tem certeza que deseja excluir esta manifestação?",
                title: "Excluir manifestação",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            if (this.page.isNew || !manifestation.id) {
                this.dataset.data.manifestations.splice(index, 1);
                return;
            }

            try {
                await axios.delete(`/api/admin/proceedings/manifestations/${manifestation.id}`);
                this.$dialog.notify.info("Manifestação removida com sucesso!", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        // ---- Comments ----

        async addComment() {
            if (!this.newComment) return;

            this.dataForm.commentLoading = true;

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/comments`, {
                    content: this.newComment,
                });
                this.$dialog.notify.info("Comentário adicionado!", { position: "top-right", timeout: 2000 });
                this.newComment = "";
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.commentLoading = false;
            }
        },

        // ---- QGC ----

        async applyQgc() {
            const proceedAction = await this.$dialog.confirm({
                text: "Deseja autorizar a alteração no QGC com base na sentença?",
                title: "Autorizar alteração QGC",
                actions: { false: "Cancelar", true: "Autorizar" },
            });

            if (!proceedAction) return;

            this.dataForm.applyQgcLoading = true;

            try {
                await axios.post(`/api/admin/proceedings/${this.$route.params.id}/apply-qgc`);
                this.$dialog.notify.info("Alteração QGC autorizada com sucesso!", { position: "top-right", timeout: 2000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.dataForm.applyQgcLoading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.btn-toggle-gap .v-btn {
    margin-right: 8px !important;
}
</style>
